import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share, Plus, MoreVertical, Download, Smartphone, Sparkles, Check } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_web-pulse-4/artifacts/i5z1wfjq_WTFH%20logo.png';

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}
function isAndroid() {
  return /android/i.test(navigator.userAgent);
}
function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const platform = isIos() ? 'ios' : isAndroid() ? 'android' : 'desktop';

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem('wtf_install_confirmed')) return;
    if (localStorage.getItem('wtf_token')) return; // logged-in users have seen the flow
    // Once-per-session cap (May 2026 iter 60): if the user has dismissed the
    // prompt this session via "Maybe later" or X, do NOT re-show until they
    // reload/come back tomorrow.
    if (sessionStorage.getItem('wtf_install_dismissals')) return;

    const timer = setTimeout(() => setShow(true), 2000);
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleNativeInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      localStorage.setItem('wtf_install_confirmed', '1');
      setShow(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    // Once-per-session: any dismissal (X or "Maybe later") writes the flag and
    // we never re-show in this session. Cleared on browser/tab close.
    sessionStorage.setItem('wtf_install_dismissals', '1');
    setShow(false);
  };

  const handleAlreadyInstalled = () => {
    // "I've already installed it" → permanent dismiss (across sessions).
    localStorage.setItem('wtf_install_confirmed', '1');
    setShow(false);
  };

  const handleGotIt = () => {
    // After viewing the steps, user is closing knowingly — treat as
    // permanent dismiss (same as "I've already installed it").
    localStorage.setItem('wtf_install_confirmed', '1');
    setShow(false);
  };

  if (!show) return null;

  // Platform-specific steps — same 3-step shape, content adapts to OS.
  const steps = platform === 'ios'
    ? [
        { Icon: Share, title: 'Tap Share',          body: 'Open the share sheet in your browser.' },
        { Icon: Plus,  title: 'Add to Home Screen', body: 'Scroll down and pick it from the list.' },
        { Icon: Smartphone, title: 'Launch like an app', body: 'Tap the icon. Full-screen, instant.' },
      ]
    : platform === 'android'
    ? [
        { Icon: MoreVertical, title: 'Open menu',         body: 'Tap the 3-dot menu in your browser.' },
        { Icon: Download,     title: 'Install app',       body: 'Pick "Install app" from the menu.' },
        { Icon: Smartphone,   title: 'Launch like an app', body: 'Tap the icon. Full-screen, instant.' },
      ]
    : [
        { Icon: Download, title: 'Install in browser',  body: 'Click the install icon in the address bar.' },
        { Icon: Plus,     title: 'Confirm install',     body: 'Or pick "Install WTFHappened" from the menu.' },
        { Icon: Smartphone, title: 'Launch like an app', body: 'Open from your desktop or app launcher.' },
      ];

  // Slot accents for the three icon tiles (matches screenshot exactly).
  const TILE_COLORS = [
    { bg: 'rgba(124, 58, 237, 0.18)',  border: 'rgba(167,139,250,0.45)', icon: '#C4B5FD' }, // purple
    { bg: 'rgba(16, 185, 129, 0.16)',  border: 'rgba(52,211,153,0.45)',  icon: '#34D399'  }, // green
    { bg: 'rgba(239, 68, 68, 0.16)',   border: 'rgba(248,113,113,0.45)', icon: '#F87171'  }, // red
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center"
        onClick={handleDismiss}
      >
        <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />

        <motion.div
          initial={{ y: 80, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 80, opacity: 0, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="install-prompt"
          className="relative w-[88%] max-w-sm mx-auto mb-4 sm:mb-0 rounded-3xl overflow-hidden"
          style={{
            background: '#0A0A0F',
            // Bright blue outline (May 2026 user request) + glow.
            border: '2px solid #3B82F6',
            // 60vh cap so the popup never dominates the viewport on mobile —
            // becomes scrollable internally if content overflows (iter 58 fix).
            maxHeight: '72vh',
            boxShadow: '0 0 0 1px rgba(59,130,246,0.30) inset, 0 24px 60px rgba(0,0,0,0.55), 0 0 36px rgba(59,130,246,0.35)',
          }}
        >
          {/* Close X */}
          <button
            data-testid="install-dismiss"
            onClick={handleDismiss}
            className="absolute top-3 right-3 z-10 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={18} strokeWidth={2.25} />
          </button>

          <div className="px-5 pt-5 pb-5 overflow-y-auto" style={{ maxHeight: '72vh' }}>
            {/* Logo + brand row */}
            <div className="flex items-center justify-center gap-1.5 mb-3">
              <img src={LOGO_URL} alt="" className="h-7 object-contain" />
              <span
                className="text-[14px] font-black tracking-tight"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                <span style={{ color: '#fff' }}>WTF</span>
                <span style={{ color: '#EF4444' }}>Happened</span>
              </span>
            </div>

            {/* Heading — gradient per screenshot, scaled down for mobile-fit */}
            <h2
              className="font-black leading-[1.0] mb-4 text-center"
              style={{
                fontFamily: 'Unbounded, sans-serif',
                fontSize: 'clamp(22px, 6vw, 30px)',
                letterSpacing: '-0.02em',
              }}
            >
              <span style={{ color: '#fff' }}>Add to </span>
              <span
                style={{
                  background: 'linear-gradient(95deg,#FF4D4D 0%,#FB923C 45%,#FACC15 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                Home
              </span>{' '}
              <span
                style={{
                  background: 'linear-gradient(95deg,#FACC15 0%,#F472B6 50%,#A855F7 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                Screen
              </span>
            </h2>

            {/* If the browser supports the native prompt, show a one-tap button
                instead of the 3-step instructions. */}
            {deferredPrompt ? (
              <button
                data-testid="install-accept"
                onClick={handleNativeInstall}
                className="w-full py-3 rounded-full text-white font-black text-[15px] flex items-center justify-center gap-2 mb-2 transition-transform active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(95deg,#FF4D6D 0%,#C026D3 50%,#6366F1 100%)',
                  fontFamily: 'Manrope, sans-serif',
                  boxShadow: '0 8px 22px rgba(192,38,211,0.40), 0 0 0 1px rgba(255,255,255,0.06) inset',
                }}
              >
                <Download size={16} strokeWidth={2.5} /> Install instantly
              </button>
            ) : (
              <div className="space-y-2 mb-4" data-testid="install-steps">
                {steps.map((s, i) => {
                  const c = TILE_COLORS[i];
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-2.5 rounded-xl"
                      style={{
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.07)',
                      }}
                    >
                      <div
                        className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                        style={{ background: c.bg, border: `1px solid ${c.border}` }}
                      >
                        <s.Icon size={20} style={{ color: c.icon }} strokeWidth={2.25} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 mb-0.5">
                          <span
                            className="text-[10px] font-bold tracking-[0.18em] tabular-nums"
                            style={{ color: '#9CA3AF', fontFamily: 'JetBrains Mono, monospace' }}
                          >
                            0{i + 1}
                          </span>
                          <span
                            className="text-[14px] font-black text-white leading-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                          >
                            {s.title}
                          </span>
                        </div>
                        <p
                          className="text-[12px] text-white/70 leading-snug font-medium"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          {s.body}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Got it! CTA — primary action: user just walked the steps */}
            {!deferredPrompt && (
              <button
                data-testid="install-got-it"
                onClick={handleGotIt}
                className="w-full py-3 rounded-full text-white font-black text-[15px] flex items-center justify-center gap-2 transition-transform active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(95deg,#FF4D6D 0%,#C026D3 50%,#6366F1 100%)',
                  fontFamily: 'Manrope, sans-serif',
                  boxShadow: '0 8px 22px rgba(192,38,211,0.40), 0 0 0 1px rgba(255,255,255,0.06) inset',
                }}
              >
                <Download size={16} strokeWidth={2.5} /> Got it!
              </button>
            )}

            {/* Already-installed escape hatch — separate from "Maybe later" so
                users with the app already on their home screen can opt out
                permanently across sessions (May 2026 iter 60 user request).
                We can't detect installation from inside a browser tab on
                privacy-conscious browsers, so this is the cleanest fix. */}
            <button
              data-testid="install-already-done"
              onClick={handleAlreadyInstalled}
              className="w-full mt-2 py-2.5 rounded-full text-[13px] font-black flex items-center justify-center gap-1.5 transition-colors"
              style={{
                background: 'rgba(16,185,129,0.10)',
                border: '1px solid rgba(16,185,129,0.40)',
                color: '#34D399',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              <Check size={14} strokeWidth={2.75} /> I've already installed it
            </button>

            {/* Maybe later — once per session only */}
            <button
              data-testid="install-maybe-later"
              onClick={handleDismiss}
              className="w-full mt-2.5 text-[12px] text-white/55 hover:text-white/85 transition-colors font-medium"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Maybe later
            </button>

            {/* Footer chip */}
            <div className="flex items-center justify-center gap-1.5 mt-3 pt-3"
              style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <Sparkles size={11} style={{ color: '#FACC15' }} strokeWidth={2.5} />
              <span
                className="text-[9px] font-black uppercase tracking-[0.18em] text-white/65"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Works offline · No app store needed
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
